import React, { useEffect, useState } from "react";
import { Truck, Users, MapPin, DollarSign, Map } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { key: "orders", label: "Orders", icon: Truck },
  { key: "partners", label: "Partners", icon: Users },
  { key: "drivers", label: "Drivers", icon: Truck },
  { key: "franchisees", label: "Franchisees", icon: MapPin },
  { key: "earnings", label: "Earnings", icon: DollarSign },
  { key: "live-map", label: "Live map", icon: Map },
];

function fmtUsd(cents) {
  if (cents === null || cents === undefined) return "—";
  return `$${(cents / 100).toFixed(2)}`;
}

async function getJson(path) {
  const r = await fetch(`${API}${path}`, { credentials: "include" });
  if (!r.ok) throw new Error(`${r.status}`);
  return r.json();
}

export default function AffiliateSuppliesView() {
  const [tab, setTab] = useState("orders");
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const path = `/api/affiliate/me/${tab}`;
        const json = await getJson(path);
        if (!cancelled) setData((d) => ({ ...d, [tab]: json }));
      } catch (e) {
        if (!cancelled) setErr("Could not load this view.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tab]);

  return (
    <div className="p-6 lg:p-10" data-testid="affiliate-supplies-view">
      <div className="text-xs uppercase tracking-[0.2em] text-lifted-green mb-2">
        Lifted Supplies · Your scope
      </div>
      <h1 className="font-anton uppercase text-4xl text-white">My Supplies</h1>
      <p className="text-lifted-dim text-sm mt-2 max-w-2xl">
        Only your own attributed orders, drivers, partners, and franchisees appear here.
        Strict cross-affiliate isolation is enforced server-side.
      </p>

      <div className="flex flex-wrap gap-2 mt-6 mb-4">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`supplies-tab-${t.key}`}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs uppercase tracking-wider font-bebas ${
                tab === t.key
                  ? "bg-lifted-green/20 border-lifted-green text-lifted-green"
                  : "bg-black/30 border-white/10 text-lifted-dim hover:border-white/30"
              }`}
            >
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>

      {loading && <p className="text-lifted-dim">Loading…</p>}
      {err && <p className="text-red-300">{err}</p>}

      {/* Orders */}
      {tab === "orders" && data.orders && (
        <div className="glass-panel p-4">
          <Counter count={data.orders.count} label="attributed orders" />
          <Table
            empty="No attributed orders yet."
            rows={data.orders.items || []}
            columns={[
              { key: "id", label: "Order", render: (o) => (o.id || "").slice(0, 8) },
              { key: "status", label: "Status" },
              { key: "customer_name", label: "Customer" },
              { key: "total_cents", label: "Total", render: (o) => fmtUsd(o.total_cents) },
              { key: "created_at", label: "Created" },
            ]}
          />
        </div>
      )}

      {/* Partners */}
      {tab === "partners" && data.partners && (
        <div className="glass-panel p-4">
          <Counter count={data.partners.count} label="referred partner stores" />
          <Table
            empty="No referred partners yet."
            rows={data.partners.items || []}
            columns={[
              { key: "name", label: "Store" },
              { key: "city", label: "City" },
              { key: "state", label: "State" },
              { key: "status", label: "Status" },
            ]}
          />
        </div>
      )}

      {/* Drivers */}
      {tab === "drivers" && data.drivers && (
        <div className="glass-panel p-4">
          <Counter count={data.drivers.count} label="referred drivers" />
          <Table
            empty="No referred drivers yet."
            rows={data.drivers.items || []}
            columns={[
              { key: "name", label: "Driver" },
              { key: "availability", label: "Availability" },
              { key: "status", label: "Status" },
              { key: "background_check", label: "Background" },
            ]}
          />
        </div>
      )}

      {/* Franchisees */}
      {tab === "franchisees" && data.franchisees && (
        <div className="glass-panel p-4">
          <Counter count={data.franchisees.count} label="referred franchisees" />
          {data.franchisees.items?.length > 0 && (
            <div className="mb-3 text-xs px-3 py-2 rounded bg-amber-500/10 border border-amber-500/30 text-amber-200">
              Master admin must set the franchise commission % before any franchise earnings post.
            </div>
          )}
          <Table
            empty="No referred franchisees yet."
            rows={data.franchisees.items || []}
            columns={[
              { key: "name", label: "Franchisee" },
              { key: "territory_count", label: "Territories", render: (f) => (f.territories || []).length },
              { key: "status", label: "Status" },
            ]}
          />
        </div>
      )}

      {/* Earnings */}
      {tab === "earnings" && data.earnings && (
        <div className="glass-panel p-4">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
            {Object.entries(data.earnings.totals_cents || {}).map(([k, v]) => (
              <div key={k} className="bg-black/30 border border-white/10 rounded-lg p-3">
                <p className="text-[10px] uppercase tracking-wider text-lifted-dim">{k}</p>
                <p className="font-anton text-xl text-white">{fmtUsd(v)}</p>
              </div>
            ))}
          </div>
          <Table
            empty="No earnings yet."
            rows={data.earnings.items || []}
            columns={[
              { key: "id", label: "Entry", render: (e) => (e.id || "").slice(0, 8) },
              { key: "source", label: "Source" },
              { key: "amount_cents", label: "Amount", render: (e) => fmtUsd(e.amount_cents) },
              { key: "status", label: "Status" },
              { key: "created_at", label: "Created" },
            ]}
          />
        </div>
      )}

      {/* Live map */}
      {tab === "live-map" && data["live-map"] && (
        <div className="glass-panel p-5">
          <div className="flex items-center gap-2 mb-4">
            <Counter count={(data["live-map"].orders || []).length} label="active orders" />
            <Counter count={(data["live-map"].drivers || []).length} label="your drivers" />
          </div>
          {!data["live-map"].tomtom_key_present && (
            <div className="mb-4 text-xs px-3 py-2 rounded bg-amber-500/10 border border-amber-500/30 text-amber-200">
              <strong>Connection required:</strong> Set <code>TOMTOM_API_KEY</code> in the backend
              env to render the live map tiles. The list below still shows attributed activity.
            </div>
          )}
          <h3 className="font-bebas tracking-wider text-white mb-2">Active orders</h3>
          <ul className="divide-y divide-white/5 mb-5">
            {(data["live-map"].orders || []).map((o) => (
              <li key={o.id} className="py-2 text-sm flex justify-between">
                <span className="text-white">
                  {(o.id || "").slice(0, 8)} · {o.status}
                </span>
                <span className="text-lifted-dim">
                  {o.driver_name || "Unassigned"}
                </span>
              </li>
            ))}
            {(data["live-map"].orders || []).length === 0 && (
              <li className="py-2 text-lifted-dim text-sm">No active orders right now.</li>
            )}
          </ul>
          <h3 className="font-bebas tracking-wider text-white mb-2">Your drivers</h3>
          <ul className="divide-y divide-white/5">
            {(data["live-map"].drivers || []).map((d) => (
              <li key={d.id} className="py-2 text-sm flex justify-between">
                <span className="text-white">{d.name}</span>
                <span className="text-lifted-dim">{d.availability}</span>
              </li>
            ))}
            {(data["live-map"].drivers || []).length === 0 && (
              <li className="py-2 text-lifted-dim text-sm">
                No drivers attached to your attributed orders.
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

function Counter({ count, label }) {
  return (
    <span className="inline-flex items-baseline gap-1 mr-4">
      <span className="font-anton text-2xl text-white">{count ?? 0}</span>
      <span className="text-xs uppercase tracking-wider text-lifted-dim">{label}</span>
    </span>
  );
}

function Table({ rows, columns, empty }) {
  if (!rows || rows.length === 0) {
    return <p className="text-lifted-dim text-sm py-4">{empty}</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wider text-lifted-dim border-b border-white/10">
            {columns.map((c) => (
              <th key={c.key} className="py-2 pr-3 font-normal">
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5">
          {rows.map((row, i) => (
            <tr key={i}>
              {columns.map((c) => (
                <td key={c.key} className="py-2 pr-3 text-white">
                  {c.render ? c.render(row) : row[c.key] ?? "—"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
